import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { Disclaimer } from "@/components/Disclaimer";
import { api, money, weekly } from "@/lib/api";
import { Loader2 } from "lucide-react";

const STATES = [
  { k: "NSW", l: "New South Wales" }, { k: "VIC", l: "Victoria" }, { k: "QLD", l: "Queensland" },
  { k: "WA", l: "Western Australia" }, { k: "SA", l: "South Australia" },
];

export default function Budget() {
  const { session, data, next, back, mergeData } = useJourney();
  const [state, setState] = useState(data.budget?.state || "NSW");
  const [fhb, setFhb] = useState(data.budget?.first_home_buyer ?? true);
  const [land, setLand] = useState(data.budget?.land_price || "");
  const [borrowing, setBorrowing] = useState(
    data.budget?.borrowing ?? data.finance?.borrowing_mid ?? ""
  );
  const [result, setResult] = useState(data.budget || null);
  const [loading, setLoading] = useState(false);

  const fLow = data.finance?.borrowing_low;
  const fHigh = data.finance?.borrowing_high;

  const calc = async () => {
    if (!land) { toast.error("Enter a land price to continue."); return; }
    setLoading(true);
    try {
      const payload = { state, first_home_buyer: fhb, land_price: Number(land) };
      if (borrowing !== "" && borrowing != null) payload.borrowing = Number(borrowing);
      const r = await api.budget(session.id, payload);
      setResult(r);
      mergeData({ budget: r });
    } catch (e) {
      toast.error("Could not build the budget.");
    } finally {
      setLoading(false);
    }
  };

  const Line = ({ label, value, sub }) => (
    <div className="flex items-baseline justify-between border-b border-line py-3">
      <span className="text-forest/80 text-sm">{label}{sub && <span className="text-mutedText text-xs ml-2">{sub}</span>}</span>
      <span className="font-medium text-forest tabular-nums">{value}</span>
    </div>
  );

  return (
    <StageFrame testId="stage-budget" className="flex flex-col lg:flex-row pt-16 pb-28">
      <div className="lg:w-[44%] px-6 md:px-12 py-8 border-r border-line">
        <Eyebrow>Your Budget</Eyebrow>
        <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-forest mt-3">Land, costs, and your home budget.</h1>

        <div className="mt-8">
          <label className="label-eyebrow text-mutedText">Borrowing to use</label>
          <div className="mt-1.5 flex items-center rounded-xl border border-line bg-white/5 px-3 focus-within:ring-2 focus-within:ring-brand">
            <span className="text-mutedText">$</span>
            <input data-testid="budget-borrowing-input" type="number" value={borrowing} onChange={(e) => setBorrowing(e.target.value)} placeholder="504000"
              className="w-full bg-transparent px-2 py-3 text-forest focus:outline-none" />
          </div>
          {fLow && fHigh && (
            <p className="text-xs text-mutedText mt-1.5">
              Indicative range {money(fLow)} – {money(fHigh)}. Adjust to find a weekly repayment that feels comfortable.
            </p>
          )}
        </div>

        <div className="mt-6">
          <label className="label-eyebrow text-mutedText">State</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {STATES.map((s) => (
              <button key={s.k} data-testid={`budget-state-${s.k}`} onClick={() => setState(s.k)}
                className={`rounded-full border px-4 py-2 text-sm transition ${state === s.k ? "border-brand bg-brand text-white" : "border-line text-forest/70"}`}>{s.k}</button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between rounded-xl border border-line bg-white/5 px-4 py-3">
          <span className="text-sm text-forest">First-home buyer?</span>
          <button data-testid="budget-fhb-toggle" onClick={() => setFhb((v) => !v)}
            className={`h-6 w-11 rounded-full transition ${fhb ? "bg-brand" : "bg-line"}`}>
            <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${fhb ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>

        <div className="mt-5">
          <label className="label-eyebrow text-mutedText">Land price</label>
          <div className="mt-1.5 flex items-center rounded-xl border border-line bg-white/5 px-3 focus-within:ring-2 focus-within:ring-brand">
            <span className="text-mutedText">$</span>
            <input data-testid="budget-land-input" type="number" value={land} onChange={(e) => setLand(e.target.value)} placeholder="360000"
              className="w-full bg-transparent px-2 py-3 text-forest focus:outline-none" />
          </div>
        </div>

        <button data-testid="budget-calc-btn" onClick={calc} disabled={loading}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-white text-sm font-medium transition hover:bg-brand disabled:opacity-50">
          {loading ? <><Loader2 size={16} className="animate-spin" /> Building…</> : "Calculate home budget"}
        </button>
      </div>

      <div className="lg:w-[56%] px-6 md:px-14 py-10 flex flex-col justify-center">
        <AnimatePresence mode="wait">
          {result ? (
            <motion.div key="r" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} data-testid="budget-result">
              <Eyebrow>Estimated weekly repayment</Eyebrow>
              <div className="font-serif font-light text-brand leading-none mt-2" style={{ fontSize: "clamp(3.5rem, 9vw, 7.5rem)" }} data-testid="budget-repayment-hero">
                {weekly(result.repayment_weekly)}<span className="text-3xl text-forest/50">/wk</span>
              </div>

              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-xl">
                <div className="rounded-xl border border-line bg-white/5 p-4">
                  <span className="label-eyebrow text-mutedText">Property value</span>
                  <p className="font-serif text-2xl text-forest mt-1 tabular-nums" data-testid="budget-property-value">{money(result.property_value)}</p>
                  <span className="text-[11px] text-mutedText">land + home</span>
                </div>
                <div className="rounded-xl border border-line bg-white/5 p-4">
                  <span className="label-eyebrow text-mutedText">Loan amount</span>
                  <p className="font-serif text-2xl text-forest mt-1 tabular-nums" data-testid="budget-loan">{money(result.loan_needed)}</p>
                  <span className="text-[11px] text-mutedText">what you borrow</span>
                </div>
                <div className="rounded-xl border border-line bg-white/5 p-4">
                  <span className="label-eyebrow text-mutedText">Your deposit</span>
                  <p className="font-serif text-2xl text-forest mt-1 tabular-nums" data-testid="budget-deposit">{money(result.deposit)}</p>
                  <span className="text-[11px] text-mutedText">funds available</span>
                </div>
                <div className={`rounded-xl border p-4 ${result.lvr > 80 ? "border-brand/50 bg-brand/10" : "border-line bg-white/5"}`}>
                  <span className="label-eyebrow text-mutedText">LVR</span>
                  <p className="font-serif text-2xl text-brand mt-1 tabular-nums" data-testid="budget-lvr">{result.lvr}%</p>
                  <span className="text-[11px] text-mutedText">{result.lvr > 80 ? "LMI likely > 80%" : "no LMI ≤ 80%"}</span>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-line bg-white/5 p-6 max-w-xl">
                <Line label="Indicative borrowing" value={money(result.borrowing)} />
                <Line label="Your deposit" value={"+ " + money(result.deposit)} />
                {result.grant > 0 && <Line label="First-home grant" value={"+ " + money(result.grant)} />}
                <Line label="Land price" value={"– " + money(result.land_price)} />
                <Line label="Stamp duty" sub={fhb ? "(FHB concession applied)" : ""} value={"– " + money(result.allowances.stamp_duty)} />
                {result.lmi > 0 && (
                  <Line label="LMI (funds to complete)" sub={`at ${result.lvr}% LVR`} value={"– " + money(result.lmi)} />
                )}
                <div className="flex items-baseline justify-between pt-4">
                  <span className="font-serif text-2xl text-forest">Your home budget</span>
                  <span className="font-serif text-3xl text-brand tabular-nums" data-testid="budget-home-budget">{money(result.home_budget)}</span>
                </div>
                <p className="text-xs text-mutedText mt-2">Home build budget (excludes land & stamp duty). Total property value ≈ {money(result.property_value)}.</p>
              </div>
              <Disclaimer short className="mt-5 max-w-xl" />
            </motion.div>
          ) : (
            <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center text-mutedText/70">
              <p className="font-serif text-2xl">Enter a land price to reveal the home budget.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ConsultantNav testId="stage-budget" onBack={back} onNext={result ? next : null} nextLabel="Reveal your three homes" disabled={!result} />
    </StageFrame>
  );
}
